/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnDestroy, type OnInit, Component, Input } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CORE_CONFIG, EventService } from '@dm/core';
import { EventServiceKeyE } from 'app/common/constants/event-service-key';
import { AuthService } from 'app/common/services/auth.service';
import { type Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { type GuideConfig } from '../smart-assistant';
import { GuideEventKey, GuideStorageKey } from '../smart-assistant.const';
import { SmartAssistantService } from '../smart-assistant.service';
import { SmartAssistantStrService } from '../smart-assistant-str.service';

@Component({
    selector: 'app-smart-assistant-switch',
    templateUrl: './smart-assistant-switch.component.html',
    styleUrls: ['./smart-assistant-switch.component.scss'],
})
export class SmartAssistantSwitchComponent implements OnInit, OnDestroy {
    @Input() tooltip: string;
    /**
     * 只有header组件上的图标才能点击
     */
    @Input() canClick = false;
    /**
     * title字体要大一些
     */
    @Input() isTitle = false;
    /**
     * 跟在图标后的字
     */
    @Input() content: string;
    /**
     * 是否显示icon
     */
    @Input() showIcon = true;
    i18n = this.str.i18n;
    /**
     * 智能助手首页的开启状态
     */
    openStatus = false;
    /**
     * 监听路由是否切回“标准模式”
     */
    router$: Subscription;
    /**
     * 监听事件
     */
    event$: Subscription;

    constructor(
        public str: SmartAssistantStrService,
        private eventService: EventService,
        private service: SmartAssistantService,
        private router: Router,
        private authService: AuthService,
    ) { }

    ngOnInit(): void {
        if (this.canClick && !this.router$) {
            this.router$ = this.router.events.pipe(filter((event) => event instanceof NavigationEnd))
                .subscribe((event: NavigationEnd) => {
                    // 路由不包含simple说明不是极简模式
                    if (!event.url.includes('/simple')) {
                        this.service.setGuideConfig(GuideStorageKey.showSmartAssistant, false);
                    }
                });
        }

        if (this.canClick && !this.event$) {
            this.event$ = this.eventService.subscribe(GuideEventKey.assistantConfigChange, (params: GuideConfig) => {
                if (this.openStatus !== params.showSmartAssistant) {
                    this.openStatus = params.showSmartAssistant;
                }
            });
        }

        // 如果第一次进入极简模式且已经看过极简模式Tip，则自动显示智能助手
        if (this.canClick && this.service.isFirstEnterSimpleMode(CORE_CONFIG.userId)) {
            this.autoShowAssistant();
        }

        // 页面刷新后，SessionStorage里还有缓存
        if (this.canClick && this.service.getGuideConfig(GuideStorageKey.showSmartAssistant)) {
            if (!this.openStatus) {
                this.statusChange();
            }

            // 先展示助手首页，因此停止指导（等用户选择是否继续）
            this.service.initStatus();
        }
    }

    /**
     * 如果第一次进入极简模式，需要自动显示智能助手
     */
    autoShowAssistant(): void {
        const callBack = (): void => {
            if (!this.openStatus) {
                this.statusChange();
            }

            // 标记用户已进入过极简模式，下次不自动显示
            this.service.setEnteredSimpleModeTag(CORE_CONFIG.userId);
        };

        if (!this.service.getUserHideTip() && this.authService.getShowSimpleModeTip()) {
            // 需要在极简模式Tip隐藏后再显示智能助手
            const simpleModeTip$ = this.eventService.subscribe(EventServiceKeyE.SimpleModeTipHide, () => {
                simpleModeTip$.unsubscribe();
                callBack();
            });

            return;
        }

        callBack();
    }

    statusChange(): void {
        if (this.canClick) {
            this.service.setGuideConfig(GuideStorageKey.showSmartAssistant, !this.openStatus);
        }
    }

    ngOnDestroy(): void {
        this.router$?.unsubscribe();
        this.event$?.unsubscribe();
    }
}
